import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { maskString } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting.ts';
import {
	CANONICAL_IDENTIFIER_KEYS,
	NAME_IDENTIFIER_KEYS,
	SENSITIVE_IDENTIFIER_KEYS,
} from '@dealstack/shared/lib/common/business/bankAccountIdentifiers';
import {
	BankAccountStatus,
	BankAccountVerificationBucketFilter,
} from '@dealstack/shared/lib/generated';

export interface BankAccountDetailLinesInput {
	bankName?: string | null;
	currency?: string | null;
	// `key` drives masking; a source that cannot supply it renders unmasked.
	identifiers?: { key?: string; label: string; value: string }[] | null;
	accountNumber?: string | null;
	iban?: string | null;
}

export interface BankAccountDetailLines {
	hasBankAccount: boolean;
	line1: string;
	// One entry per identifier, so a caller renders them on their own lines instead of
	// truncating a joined blob mid-value. The currency rides on the last one.
	detailLines: string[];
}

export { SENSITIVE_IDENTIFIER_KEYS } from '@dealstack/shared/lib/common/business/bankAccountIdentifiers';

export const maskIdentifier = (
	key: string | undefined,
	value: string | null | undefined
): string => (key && SENSITIVE_IDENTIFIER_KEYS.has(key) ? maskString(value) : value ?? '');

// Two-line rendering of an account: the bank name over its identifiers.
// Format-rule accounts list every captured identifier; classic accounts fall back
// to the account number / IBAN they were entered with.
export const getBankAccountDetailLines = ({
	bankName,
	currency,
	identifiers,
	accountNumber,
	iban,
}: BankAccountDetailLinesInput): BankAccountDetailLines => {
	const identifierLines = (identifiers ?? []).map(
		(identifier) => `${identifier.label}: ${maskIdentifier(identifier.key, identifier.value)}`
	);
	// The classic fallback is the account number or IBAN itself — both always masked.
	const classicLine = accountNumber || iban ? maskString(accountNumber || iban || '') : '';
	const hasBankAccount = !!(bankName || identifierLines.length || classicLine);
	const line1 = bankName ?? LONG_DASH;
	const lines = identifierLines.length ? identifierLines : [classicLine || LONG_DASH];
	const detailLines = hasBankAccount
		? lines.map((line, index) =>
				currency && index === lines.length - 1 ? `${line} (${currency})` : line
			)
		: [];

	return { hasBankAccount, line1, detailLines };
};

// The admin-facing shape of the lifecycle: only the three statuses the review queue
// serves have a bucket — the confirmation-funnel and dead states never reach a reviewer,
// so the server filters them out before the list is built. Must mirror STATUSES_BY_BUCKET
// in investorBankAccount.utils.ts, so the value rendered here is exactly the value the
// server filters and counts by.
const STATUS_TO_BUCKET: Partial<Record<BankAccountStatus, BankAccountVerificationBucketFilter>> = {
	[BankAccountStatus.APPROVED]: BankAccountVerificationBucketFilter.COMPLETE,
	[BankAccountStatus.PENDING_ADMIN_REVIEW]: BankAccountVerificationBucketFilter.PENDING,
	[BankAccountStatus.REJECTED]: BankAccountVerificationBucketFilter.DECLINED,
};

export const getBankAccountVerificationBucket = (
	status: BankAccountStatus
): BankAccountVerificationBucketFilter | undefined => STATUS_TO_BUCKET[status];

export const BANK_ACCOUNT_VERIFICATION_BUCKET_ORDER = [
	BankAccountVerificationBucketFilter.COMPLETE,
	BankAccountVerificationBucketFilter.PENDING,
	BankAccountVerificationBucketFilter.DECLINED,
] as const;

export const getBankAccountVerificationBucketColor = (
	bucket: BankAccountVerificationBucketFilter
): string => {
	switch (bucket) {
		case BankAccountVerificationBucketFilter.COMPLETE:
			return defaultTheme.v3.colors.teal[8];
		case BankAccountVerificationBucketFilter.PENDING:
			return defaultTheme.v3.colors.orange[6];
		case BankAccountVerificationBucketFilter.DECLINED:
			return defaultTheme.v3.colors.red[8];
	}
};

export interface BankAccountIdentifierRow {
	key: string;
	label: string;
	value: string;
}

// The account's identifying values in entry order. Labels come from the server, which
// resolves them once for every consumer.
export const getIdentifierRows = <T extends BankAccountIdentifierRow>(
	identifiers: T[] | undefined
): T[] =>
	(identifiers ?? []).filter(
		({ key, value }) => !NAME_IDENTIFIER_KEYS.has(key) && !!value.trim()
	);

// Only the identifiers no classic field covers: what a consumer rendering the classic
// five has to add for a local-format account (sort code, CLABE, PIX key) to appear at all.
export const getExtraIdentifierRows = <T extends BankAccountIdentifierRow>(
	identifiers: T[] | undefined
): T[] => getIdentifierRows(identifiers).filter(({ key }) => !CANONICAL_IDENTIFIER_KEYS.has(key));
